import { RequestStatusType, SortCondition } from 'shared/enums';
import { Pagination } from 'shared/types/types';
import { TCreateAbsenceRequest, TGetAllAbsenceRequest } from '../absence';
import { edpApiV1 } from '../index';

export type TAbsenceGetResponseSubstitution = {
  id: number;
  created: string;
  startedAt: string;
  endedAt: string;
  comment: string;
  status: {
    type: RequestStatusType;
    name: string;
  };
  employee: {
    id: number;
    name: string;
    image: any;
    division: {
      id: string;
      code: string;
      name: string;
    };
    lastName: string;
    position: {
      id: string;
      name: string;
    };
    middleName: string;
  };
};

export type TPreparedAbsenceSupervisor = {
  key: number;
  id: number;
  created: string;
  period: string;
  fio: string;
  status: {
    type: string;
    name: string;
  };
  hr: null;
  zupRequestStatus: null;
  comment: string;
};

export type TAbsenceGetResponseSubstitutionPaginated = {
  data: TAbsenceGetResponseSubstitution[];
  pagination: Pagination;
};

export type TAbsenceCreateResponse = {
  status: string;
};

export type TGetAllAbsenceRequestSubstitution = {
  page?: number;
  size?: number;
  order?: SortCondition;
  divisionId?: string;
  byId?: string;
  fio?: string;
  personnelNumber?: string;
  positionName?: string;
};

export interface TCreateAbsenceRequestSubstitution extends TCreateAbsenceRequest {
  divisionId?: string;
}

export const absenceSupervisorApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getAllAbsenceSupervisor: build.query<TAbsenceGetResponseSubstitutionPaginated, TGetAllAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...params } = data;
        return { url: '/v1/dublicate/supervisor/absence/requests', method: 'GET', params };
      },
      providesTags: ['absenceSupervisor'],
    }),
    createAbsenceSupervisor: build.mutation<TAbsenceCreateResponse, TCreateAbsenceRequestSubstitution>({
      query: body => ({
        url: `/v1/dublicate/supervisor/absence/requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absenceSupervisor'],
    }),
    getAllAbsenceDeputy: build.query<TAbsenceGetResponseSubstitutionPaginated, TGetAllAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...params } = data;
        return { url: `/v1/dublicate/deputy/absence/requests/${divisionId}`, method: 'GET', params };
      },
      providesTags: ['absenceDeputy'],
    }),
    createAbsenceDeputy: build.mutation<TAbsenceCreateResponse, TCreateAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...body } = data;
        return { url: `/v1/dublicate/deputy/absence/requests/${divisionId}`, method: 'POST', body };
      },
      invalidatesTags: ['absenceDeputy'],
    }),
    getAllAbsenceActing: build.query<TAbsenceGetResponseSubstitutionPaginated, TGetAllAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...params } = data;
        return { url: `/v1/dublicate/acting/${divisionId}/absence/requests`, method: 'GET', params };
      },
      providesTags: ['absenceActing'],
    }),
    createAbsenceActing: build.mutation<TAbsenceCreateResponse, TCreateAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...body } = data;
        return {
          url: `/v1/dublicate/acting/${divisionId}/absence/requests`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['absenceActing'],
    }),
    getAllAbsenceTimekeeper: build.query<TAbsenceGetResponseSubstitutionPaginated, TGetAllAbsenceRequestSubstitution>({
      query: data => {
        const { divisionId, ...params } = data;
        return { url: '/v1/dublicate/timekeeper/absence/requests', method: 'GET', params };
      },
      providesTags: ['absenceTimekeeper'],
    }),
    createAbsenceTimekeeper: build.mutation<TAbsenceCreateResponse, TCreateAbsenceRequestSubstitution>({
      query: body => ({
        url: `/v1/dublicate/timekeeper/absence/requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['absenceTimekeeper'],
    }),
  }),

  overrideExisting: false,
});

export const {
  useCreateAbsenceActingMutation,
  useCreateAbsenceDeputyMutation,
  useCreateAbsenceSupervisorMutation,
  useCreateAbsenceTimekeeperMutation,
  useGetAllAbsenceActingQuery,
  useGetAllAbsenceDeputyQuery,
  useGetAllAbsenceSupervisorQuery,
  useGetAllAbsenceTimekeeperQuery,
  useLazyGetAllAbsenceActingQuery,
  useLazyGetAllAbsenceDeputyQuery,
  useLazyGetAllAbsenceSupervisorQuery,
  useLazyGetAllAbsenceTimekeeperQuery,
} = absenceSupervisorApi;
